/**
 * Select tool handler
 * - Click: hit-test → select object (or deselect if clicking empty space)
 * - Shift+click: add to selection
 * - Drag on empty space: rectangle selection
 * - Drag on selected object: move it (delegates to move)
 */

import { Vec2, distanceBetween } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { hitTestAll, getObjectsInRect, getPositionsInRect } from '@/canvas/hit-test';

interface SelectToolState {
  isDragging: boolean;
  dragStart: Vec2 | null;
  selectionRect: { topLeft: Vec2; bottomRight: Vec2 } | null;
}

const state: SelectToolState = {
  isDragging: false,
  dragStart: null,
  selectionRect: null,
};

const MIN_DRAG_DISTANCE = 5; // pixels to distinguish drag from click

export function getSelectToolHandler(): ToolHandler {
  return {
    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const { layout } = useStageStore.getState();
      if (!layout) return;

      // Check if we hit something
      const hit = hitTestAll(ctx.worldPos, layout);

      if (!hit) {
        // Clicking empty space - deselect or start rectangle selection
        if (!ctx.shiftKey) {
          useEditorStore.getState().deselectAll();
        }
        state.isDragging = true;
        state.dragStart = ctx.worldPos;
        state.selectionRect = null;
      } else {
        // Hit something
        if (ctx.shiftKey) {
          // Add/remove from selection
          useEditorStore.getState().selectObject(hit.id, true);
        } else {
          // Select single object
          useEditorStore.getState().selectObject(hit.id, false);
        }
      }
    },

    onMouseMove(ctx: ToolContext) {
      const { layout } = useStageStore.getState();
      if (!layout) return;

      // Check hover for visual feedback
      const hit = hitTestAll(ctx.worldPos, layout);
      useEditorStore.getState().setHovered(hit?.id || null);

      // Handle rectangle selection drag
      if (state.isDragging && state.dragStart) {
        const distance = distanceBetween(state.dragStart, ctx.worldPos);

        if (distance > MIN_DRAG_DISTANCE) {
          // It's a drag, not a click
          const topLeft = {
            x: Math.min(state.dragStart.x, ctx.worldPos.x),
            y: Math.min(state.dragStart.y, ctx.worldPos.y),
          };
          const bottomRight = {
            x: Math.max(state.dragStart.x, ctx.worldPos.x),
            y: Math.max(state.dragStart.y, ctx.worldPos.y),
          };

          state.selectionRect = { topLeft, bottomRight };

          // Select all objects within rect
          if (!ctx.ctrlKey && !ctx.shiftKey) {
            const objectIds = getObjectsInRect(topLeft, bottomRight, layout.objects);
            const positionIds = getPositionsInRect(topLeft, bottomRight, layout.shooting_positions);
            const allIds = [...objectIds, ...positionIds];

            if (allIds.length > 0) {
              // Select first item, then add others with multi-select
              useEditorStore.getState().selectObject(allIds[0], false);
              for (let i = 1; i < allIds.length; i++) {
                useEditorStore.getState().selectObject(allIds[i], true);
              }
            }
          }
        }
      }
    },

    onMouseUp(_ctx: ToolContext) {
      state.isDragging = false;
      state.dragStart = null;
      state.selectionRect = null;
    },

    getCursor() {
      return 'default';
    },

    renderPreview(ctx2d: CanvasRenderingContext2D, zoom: number) {
      if (!state.selectionRect) return;

      const { topLeft, bottomRight } = state.selectionRect;

      // Draw selection rectangle
      ctx2d.strokeStyle = 'rgba(100, 150, 255, 0.6)';
      ctx2d.lineWidth = 2 / zoom;
      ctx2d.fillStyle = 'rgba(100, 150, 255, 0.1)';

      const width = bottomRight.x - topLeft.x;
      const height = bottomRight.y - topLeft.y;

      ctx2d.fillRect(topLeft.x, topLeft.y, width, height);
      ctx2d.strokeRect(topLeft.x, topLeft.y, width, height);
    },
  };
}
